import React from "react";
import {
  ArrowUpDown,
  Ban,
  Filter,
  LayoutGrid,
  Shield,
  SlidersHorizontal,
  User,
  UserCheck,
  UserCog,
  Users,
  X,
} from "lucide-react";
import SidebarNavIcon from "../../app/components/ui/SidebarNavIcon";
import { DESK_VIEWS, ROLE_FILTERS, SORT_OPTIONS, STATUS_FILTERS } from "./constants";

const DESK_ICONS = {
  all: LayoutGrid,
  staff: UserCog,
  banned: Ban,
};

const STATUS_ICONS = {
  all: Users,
  active: UserCheck,
  banned: Ban,
};

const ROLE_ICONS = {
  all: Users,
  user: User,
  agent: UserCog,
  ambassador: UserCog,
  partner: Shield,
  admin: Shield,
  super_admin: Shield,
};

const SORT_ICONS = {
  newest: ArrowUpDown,
  oldest: ArrowUpDown,
  name: User,
  cash: ArrowUpDown,
};

function FilterSection({ label, labelIcon: LabelIcon, children }) {
  return (
    <section className="am-nav-section">
      <p className="am-nav-section-label">
        {LabelIcon ? <LabelIcon size={10} strokeWidth={2.4} aria-hidden /> : null}
        {label}
      </p>
      <div className="am-nav-list">{children}</div>
    </section>
  );
}

function NavFilterItem({ active, onClick, icon, label, hint, count, countTone }) {
  return (
    <button
      type="button"
      className={`am-nav-item${active ? " am-nav-item-on" : ""}`}
      aria-pressed={active}
      onClick={onClick}
    >
      <span className="am-nav-item-icon">
        <SidebarNavIcon icon={icon} size={13} />
      </span>
      <span className="am-nav-item-copy">
        <strong>{label}</strong>
        {hint ? <em>{hint}</em> : null}
      </span>
      {count != null && count > 0 ? (
        <span className={`am-nav-item-count${countTone ? ` am-nav-item-count-${countTone}` : ""}`}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

export default function AdminUsersFiltersRail({
  desk,
  status,
  role,
  sortKey,
  staffCount,
  bannedCount,
  onDeskChange,
  onStatusChange,
  onRoleChange,
  onSortChange,
  onClear,
  activeCount,
  compact,
  onClose,
}) {
  const hasFilters = activeCount > 0;

  return (
    <aside className={`am-nav-rail${compact ? " am-nav-rail-compact" : ""}`}>
      <div className="am-nav-head">
        <div className="am-nav-title">
          <SlidersHorizontal size={14} strokeWidth={2.25} aria-hidden />
          <span>User desk</span>
        </div>
        {compact && onClose ? (
          <button type="button" className="am-chrome-btn am-chrome-btn-icon" onClick={onClose} aria-label="Close filters">
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div className="am-nav-scroll">
        <FilterSection label="Desk">
          {DESK_VIEWS.map((v) => (
            <NavFilterItem
              key={v.id}
              active={desk === v.id}
              onClick={() => onDeskChange(v.id)}
              icon={DESK_ICONS[v.id]}
              label={v.label}
              hint={v.hint}
              count={v.id === "staff" ? staffCount : v.id === "banned" ? bannedCount : null}
              countTone={v.id === "banned" && bannedCount > 0 ? "warn" : undefined}
            />
          ))}
        </FilterSection>

        <FilterSection label="Sort" labelIcon={ArrowUpDown}>
          {SORT_OPTIONS.map((opt) => (
            <NavFilterItem
              key={opt.value}
              active={sortKey === opt.value}
              onClick={() => onSortChange(opt.value)}
              icon={SORT_ICONS[opt.value]}
              label={opt.label}
            />
          ))}
        </FilterSection>

        <FilterSection label="Status" labelIcon={Filter}>
          {STATUS_FILTERS.map((f) => (
            <NavFilterItem
              key={f.value}
              active={status === f.value}
              onClick={() => onStatusChange(f.value)}
              icon={STATUS_ICONS[f.value]}
              label={f.label}
              count={f.value === "banned" ? bannedCount : undefined}
              countTone={f.value === "banned" && bannedCount > 0 ? "warn" : undefined}
            />
          ))}
        </FilterSection>

        <FilterSection label="Role" labelIcon={UserCog}>
          {ROLE_FILTERS.map((f) => (
            <NavFilterItem
              key={f.value}
              active={role === f.value}
              onClick={() => onRoleChange(f.value)}
              icon={ROLE_ICONS[f.value]}
              label={f.label}
            />
          ))}
        </FilterSection>
      </div>

      {hasFilters ? (
        <button type="button" className="am-nav-clear" onClick={onClear}>
          Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
        </button>
      ) : null}
    </aside>
  );
}
